// data/posts/lisbon-3-day-itinerary.js

export default {
  slug: 'lisbon-3-day-itinerary',

  category: 'Portugal Travel',

  language: 'en',

  title: 'Lisbon in 3 Days: The Perfect First-Time Itinerary',

  excerpt:
    'Planning your first trip to Lisbon? This practical 3-day Lisbon itinerary covers the city’s historic neighborhoods, viewpoints, food, attractions and essential travel tips.',

  quickAnswer:
    'Three days is enough to experience Lisbon’s main highlights, including Alfama, Baixa, Belém and several of the city’s famous viewpoints, while still leaving time for food and relaxed exploration.',

  seo: {
    title: 'Lisbon in 3 Days: The Perfect First-Time Itinerary',
    description:
      'A practical 3-day Lisbon itinerary covering the best things to see, where to eat, neighborhoods to explore and useful tips for first-time visitors.',
    canonical:
      'https://guiders.pt/guides/lisbon-3-day-itinerary',
    image:
      'https://guiders.pt/img/share/guiders-portugal.webp'
  },

  keywords: [
    'Lisbon itinerary',
    'Lisbon 3 day itinerary',
    'things to do in Lisbon',
    'Lisbon travel guide',
    'Portugal travel'
  ],

  author: {
    name: 'Your Name',
    url: '/about',
    image: '/images/authors/your-name.jpg'
  },

  publishedAt: '2026-08-20',
  updatedAt: '2026-08-20',

  hero: {
    src: '/images/lisbon-3-day-itinerary.jpg',
    alt: 'Lisbon rooftops and the Tagus River at sunset',
    caption:
      'Lisbon’s hills, colorful buildings and views over the Tagus River make the city especially rewarding to explore on foot.',
    sizes: '(max-width: 900px) 100vw, 1600px',

    srcset: {
      640: '/images/lisbon-640.jpg',
      1024: '/images/lisbon-1024.jpg',
      1600: '/images/lisbon-1600.jpg'
    }
  },

  introduction: [
    'Lisbon is one of those European cities that rewards a slower pace. Its historic neighborhoods, tiled buildings, viewpoints and food markets are packed into a relatively compact area, but the steep streets mean you should plan your days carefully.',

    'If this is your first visit, three days gives you enough time to see the major sights without turning the trip into a checklist.'
  ],

  sections: [
    {
      id: 'day-1',
      title: 'Day 1: Explore Alfama and Lisbon’s Historic Center',

      paragraphs: [
        'Start your first morning in Alfama, one of Lisbon’s oldest neighborhoods. The narrow streets and steep stairways are best explored before the busiest part of the day.',

        'From Alfama, continue towards Baixa and Praça do Comércio. This gives you a natural introduction to the historic center before ending the afternoon around Chiado and Bairro Alto.'
      ],

      bullets: [
        'Start with Alfama in the morning',
        'Visit Lisbon Cathedral',
        'Walk towards Praça do Comércio',
        'Explore Chiado',
        'Watch sunset from a miradouro'
      ]
    },

    {
      id: 'day-2',
      title: 'Day 2: Belém and the Tagus Waterfront',

      paragraphs: [
        'Spend your second day in Belém, west of central Lisbon. The waterfront area is home to some of the city’s most recognizable monuments and museums.',

        'Allow plenty of time rather than trying to visit every attraction. Belém works particularly well as a relaxed half-day or full-day excursion.'
      ],

      bullets: [
        'Visit Jerónimos Monastery',
        'Walk along the waterfront',
        'See Belém Tower',
        'Try a traditional pastel de nata',
        'Return to central Lisbon for dinner'
      ]
    },

    {
      id: 'day-3',
      title: 'Day 3: Viewpoints, Local Neighborhoods and Food',

      paragraphs: [
        'Use your final day to explore beyond the most famous attractions. Lisbon is at its most enjoyable when you leave room for wandering, cafés and neighborhood discoveries.',

        'Consider spending the morning around Graça and then making your way back towards the center.'
      ]
    }
  ],

  tips: [
    {
      title: 'Wear comfortable shoes',
      text:
        'Lisbon has steep hills and many streets are paved with calçada, so comfortable shoes will make a noticeable difference.'
    },

    {
      title: 'Use public transport strategically',
      text:
        'The metro is useful for longer journeys, while walking is often the best way to experience the historic neighborhoods.'
    },

    {
      title: 'Leave room for spontaneity',
      text:
        'Do not fill every hour of a Lisbon trip. Some of the best experiences come from wandering into a small square, café or viewpoint.'
    }
  ],

  faqs: [
    {
      question: 'Is three days enough for Lisbon?',

      answer:
        'Yes. Three days is enough to experience Lisbon’s major historic neighborhoods, visit Belém, see several viewpoints and enjoy the city’s food scene without rushing through every attraction.'
    },

    {
      question: 'What is the best area to stay in Lisbon for a first visit?',

      answer:
        'Baixa, Chiado and nearby central neighborhoods are convenient choices for first-time visitors because many major attractions, restaurants and public transport connections are within easy reach.'
    },

    {
      question: 'Is Lisbon easy to explore on foot?',

      answer:
        'Many central areas are walkable, but Lisbon is hilly and some streets are steep. Comfortable footwear is strongly recommended.'
    }
  ]
}